import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Landmark, Car as CarIcon, Wrench, ShieldCheck, ClipboardCheck, ArrowRight, TrendingUp, CheckCircle2 } from "lucide-react";
import { api, fmtMXN, fmtDate } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function InitialInvestment() {
    const [cars, setCars] = useState([]);
    const [carId, setCarId] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/cars").then((r) => setCars(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = carId !== "all" ? { car_id: carId } : {};
        api.get("/analytics/investment", { params }).then((r) => {
            setData(r.data);
            setLoading(false);
        });
    }, [carId]);

    return (
        <div className="space-y-6" data-testid="investment-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Capital</p>
                    <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-1 font-display">
                        Inversión inicial
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Costo de autos, reparaciones, seguros y verificaciones acumulados por vehículo.
                    </p>
                </div>
                <Select value={carId} onValueChange={setCarId}>
                    <SelectTrigger className="w-[220px] bg-white" data-testid="filter-car-investment">
                        <SelectValue placeholder="Auto" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los autos</SelectItem>
                        {cars.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                                {c.plate} · {c.brand} {c.model}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-200 shadow-sm bg-slate-900 text-white overflow-hidden" data-testid="kpi-total-investment">
                    <CardContent className="p-6 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-300">Inversión total</div>
                            <div className="text-4xl font-semibold tracking-tighter mt-2 font-display">
                                {fmtMXN(data?.total_investment || 0)}
                            </div>
                            <div className="text-xs text-slate-400 mt-2">
                                Ingresos acumulados: {fmtMXN(data?.total_income || 0)}
                            </div>
                        </div>
                        <Landmark size={40} className="text-slate-500 shrink-0" />
                    </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm" data-testid="kpi-roi">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Retorno de inversión</div>
                            <TrendingUp size={20} className={(data?.roi_percent || 0) >= 100 ? "text-emerald-600" : "text-amber-600"} />
                        </div>
                        <div className="text-4xl font-semibold tracking-tighter mt-2 font-display text-slate-900" data-testid="roi-percent-value">
                            {data?.roi_percent != null ? `${data.roi_percent}%` : "—"}
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${(data?.roi_percent || 0) >= 100 ? "bg-emerald-500" : "bg-amber-500"}`}
                                style={{ width: `${Math.min(data?.roi_percent || 0, 100)}%` }}
                            />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                            {data?.total_investment > 0
                                ? (data.total_income >= data.total_investment
                                    ? "Inversión recuperada en su totalidad."
                                    : `Faltan ${fmtMXN(data.total_investment - data.total_income)} por recuperar.`)
                                : "Sin inversión registrada aún."}
                        </p>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <InvKpi
                    label="Costo de autos"
                    value={fmtMXN(data?.total_car_value || 0)}
                    icon={<CarIcon className="text-slate-700" size={20} />}
                    testid="kpi-car-value"
                />
                <InvKpi
                    label="Reparaciones"
                    value={fmtMXN(data?.total_repairs || 0)}
                    icon={<Wrench className="text-rose-600" size={20} />}
                    testid="kpi-repairs"
                />
                <InvKpi
                    label="Seguros"
                    value={fmtMXN(data?.total_insurance || 0)}
                    icon={<ShieldCheck className="text-amber-600" size={20} />}
                    testid="kpi-insurance"
                />
                <InvKpi
                    label="Verificaciones"
                    value={fmtMXN(data?.total_verifications || 0)}
                    icon={<ClipboardCheck className="text-emerald-600" size={20} />}
                    testid="kpi-verifications"
                />
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium">Desglose por auto</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Reparaciones = movimientos de gasto en categorías de "Mantenimiento", "Mano de obra", "Refacciones", "Servicio mecánico", "Reparación" o "Llantas".</p>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-16 text-center text-slate-400 text-sm">Cargando…</div>
                    ) : (data?.by_car || []).length === 0 ? (
                        <div className="py-16 text-center text-slate-400 text-sm">Aún no hay autos registrados.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                        <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                        <th className="text-right px-4 py-3 font-semibold">Costo del auto</th>
                                        <th className="text-right px-4 py-3 font-semibold">Reparaciones</th>
                                        <th className="text-right px-4 py-3 font-semibold">Seguros</th>
                                        <th className="text-right px-4 py-3 font-semibold">Verificaciones</th>
                                        <th className="text-right px-4 py-3 font-semibold">Total invertido</th>
                                        <th className="text-right px-4 py-3 font-semibold">Ingresos acumulados</th>
                                        <th className="text-left px-4 py-3 font-semibold">Recuperación</th>
                                        <th className="px-4 py-3" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.by_car || []).map((b) => (
                                        <tr key={b.car_id} className="border-t border-slate-100" data-testid={`investment-row-${b.car_id}`}>
                                            <td className="px-4 py-3 text-slate-900 font-medium">{b.label}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(b.car_value)}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(b.repairs)}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(b.insurance)}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(b.verifications)}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmtMXN(b.total)}</td>
                                            <td className="px-4 py-3 text-right text-slate-700">{fmtMXN(b.total_income)}</td>
                                            <td className="px-4 py-3" data-testid={`investment-recovery-${b.car_id}`}>
                                                {b.recovered ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                                        <CheckCircle2 size={12} className="mr-1" /> Recuperada el {fmtDate(b.recovered_on)}
                                                    </Badge>
                                                ) : b.estimated_days_remaining != null ? (
                                                    <span className="text-xs text-amber-700">
                                                        ~{b.estimated_days_remaining} días (≈ {fmtDate(b.projected_recovery_date)})
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400">Sin datos suficientes</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Link to={`/autos/${b.car_id}`} className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900" data-testid={`investment-detail-${b.car_id}`}>
                                                    Ver auto <ArrowRight size={12} className="ml-1" />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function InvKpi({ label, value, icon, testid }) {
    return (
        <Card className="border-slate-200 shadow-sm" data-testid={testid}>
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">{label}</div>
                    {icon}
                </div>
                <div className="text-2xl md:text-3xl font-semibold tracking-tighter text-slate-900 mt-3 font-display">
                    {value}
                </div>
            </CardContent>
        </Card>
    );
}
